import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import OfferInsuranceCard from "./OfferInsuranceCard";
import {
  loadInsurance,
  loadCompanies,
  loadInsuranceProducts,
  saveInsurance,
  cancelInsuranceChanges
} from "../actionCreators";
import memoize from "lodash.memoize";
import { isMandatory, validationEngine } from "utils/simple-validations";

export const getValidationRules = () => {
  return {
    insurance: {
      selectionDone: [isMandatory()]
    }
  };
};
const initialValidationInfo = {
  isValid: true,
  insurance: { selectionDone: {} }
};

class OfferInsuranceContainer extends React.PureComponent {
  state = {
    insurance: { ...this.props.insurance, selectionDone: null },
    validationInfo: initialValidationInfo,
    products: this.props.products,
    isDirty: false,
    prevProps: {
      insurance: null,
      products: null
    }
  };

  static getDerivedStateFromProps(props, state) {
    const prevProps = state.prevProps;

    //react to insurance prop change
    if (props.insurance !== prevProps.insurance) {
      return {
        prevProps: { insurance: props.insurance, products: props.products },
        insurance: { ...props.insurance, selectionDone: null },
        products: props.products,
        isDirty: false
      };
    }

    //react to products prop change
    if (props.products !== prevProps.products) {
      return {
        prevProps: { ...prevProps, products: props.products },
        products: props.products
      };
    }

    if (props.insurance !== prevProps.insurance && !props.insuranceLoading) {
      props.actions.loadInsurance(props.documentId, props.siteId);
    }

    if (props.companies !== prevProps.companies && !props.companiesLoading) {
      props.actions.loadCompanies(props.documentId, props.siteId);
    }

    if (props.products !== prevProps.products && !props.productsLoading) {
      props.actions.loadInsuranceProducts(props.documentId, props.siteId);
    }

    return null;
  }

  componentDidMount() {
    this.props.actions.loadInsurance(this.props.documentId, this.props.siteId);
    this.props.actions.loadCompanies(this.props.documentId, this.props.siteId);
    this.props.actions.loadInsuranceProducts(this.props.documentId, this.props.siteId);
  }

  componentWillUnmount() {
    this.props.actions.cancelInsuranceChanges(this.props.documentId, this.props.siteId);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.insuranceLoading !== this.props.insuranceLoading && this.props.insuranceLoading) {
      this.props.actions.loadInsurance(this.props.documentId, this.props.siteId);
    }
    if (prevProps.companiesLoading !== this.props.companiesLoading && !this.props.companiesLoading) {
      this.props.actions.loadCompanies(this.props.documentId, this.props.siteId);
    }
    if (prevProps.productsLoading !== this.props.productsLoading && this.props.productsLoading) {
      this.props.actions.loadInsuranceProducts(this.props.documentId, this.props.siteId);
    }
  }

  handleInsurancePropertyChange = prop => value => {
    const newProducts = this.state.products.map(el =>
      el["insuranceCompanyId"] === value.insuranceCompanyId
        ? Object.assign({}, el, { selected: true })
        : Object.assign({}, el, { selected: false })
    );
    if (prop === "company") {
      this.setState(prevState => ({
        products: newProducts,
        insurance: { ...prevState.insurance, [prop]: value, selectionDone: true },
        isDirty: true
      }));
    } else {
      this.setState(prevState => ({
        insurance: { ...prevState.insurance, [prop]: value },
        isDirty: true
      }));
    }
  };

  handleProductPropertyChange = memoize(idx => _prop => value => {
    const insuranceCompanyId = this.state.products[idx].insuranceCompanyId;
    let newProducts = this.state.products;
    let newCompany = this.state.insurance.company;
    let newSelection = this.state.selectionDone;
    if (value === true) {
      newProducts = this.state.products.map(el =>
        el["insuranceCompanyId"] === insuranceCompanyId
          ? Object.assign({}, el, { selected: true })
          : Object.assign({}, el, { selected: false })
      );
      newCompany = this.props.companies.filter(
        comp => comp.insuranceCompanyId === newProducts.filter(prod => prod.selected === true)[0].insuranceCompanyId
      )[0];
      newSelection = true;
    } else if (value === false) {
      newProducts = this.state.products.map(el => Object.assign({}, el, { selected: false }));
      if (this.state.insurance.mandatory === true) newSelection = null;
      else newSelection = true;
    }
    this.setState(prevState => ({
      products: newProducts,
      insurance: {
        ...prevState.insurance,
        company: newCompany,
        selectionDone: newSelection
      },
      isDirty: true
    }));
  });

  handleSave = () => {
    let validationInfo = validationEngine.validate(this.state, getValidationRules());
    if (!validationInfo.isValid) {
      this.setState({
        validationInfo
      });
      return;
    }
    const { documentId, siteId } = this.props;
    const { insurance, products } = this.state;
    this.props.actions.saveInsurance(documentId, siteId, { ...insurance, products });
  };

  render() {
    const { companies, insuranceLoading, productsLoading, saving } = this.props;
    const { insurance, products, isDirty, validationInfo } = this.state;

    return (
      companies.length > 0 && (
        <OfferInsuranceCard
          insurance={insurance}
          products={products}
          companies={companies}
          onInsurancePropertyChange={this.handleInsurancePropertyChange}
          onProductPropertyChange={this.handleProductPropertyChange}
          insuranceLoading={insuranceLoading}
          productsLoading={productsLoading}
          onSave={this.handleSave}
          saving={saving}
          isDirty={isDirty}
          isValid={validationInfo.isValid}
        />
      )
    );
  }
}

OfferInsuranceContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  insurance: PropTypes.object.isRequired,
  products: PropTypes.array,
  insuranceLoading: PropTypes.bool.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  actions: PropTypes.object,
  companies: PropTypes.array,
  companiesLoading: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  return {
    insurance: state.currentOffer.insuranceData.insurance,
    products: state.currentOffer.insuranceData.products || state.currentOffer.insuranceData.insurance.products,
    companies: state.currentOffer.insuranceData.companies,
    insuranceLoading: !state.currentOffer.insuranceData.insuranceLoaded,
    productsLoading: !state.currentOffer.insuranceData.productsLoaded,
    saving: state.currentOffer.insuranceData.saving,
    companiesLoading: state.currentOffer.insuranceData.companiesLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadInsurance,
        loadCompanies,
        loadInsuranceProducts,
        saveInsurance,
        cancelInsuranceChanges
      },
      dispatch
    )
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OfferInsuranceContainer);
